import { memo, type FC } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { EmptyState } from "../../../../components/EmptyState";
import { TopicSuggestions } from "../../components/TopicSuggestions";
import { TreeView } from "@/components/filters/TreeSelect/TreeView/TreeView";
import { SELECTION_PRESETS } from "@/components/filters/TreeSelect/hooks/useHierarchicalSelection";
import type { TreeViewBaseItem } from "@mui/x-tree-view/models";
import type { CachedTopic } from "../../slice";

type TopicsSearchTabProps = {
  inputValue: string;
  onInputChange: (value: string) => void;
  onClearInput: () => void;
  minChars: number;

  shouldSearch: boolean;
  searchQuery: string;
  searchLoading: boolean;
  resultsCount: number;

  searchItems: TreeViewBaseItem[];
  treeSelectedItems: string[];
  searchExpandedItems: string[];
  onSelectionChange: (itemIds: string[]) => void;

  selectedTopicIds: string[];
  onAddSuggestedTopic: (topic: CachedTopic) => void;
};

export const TopicsSearchTab: FC<TopicsSearchTabProps> = memo(
  ({
    inputValue,
    onInputChange,
    onClearInput,
    minChars,
    shouldSearch,
    searchQuery,
    searchLoading,
    resultsCount,
    searchItems,
    treeSelectedItems,
    searchExpandedItems,
    onSelectionChange,
    selectedTopicIds,
    onAddSuggestedTopic,
  }) => {
    return (
      <Box sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          id="intent-topics-search-input"
          placeholder="Search topics..."
          size="small"
          fullWidth
          autoFocus
          value={inputValue}
          onChange={(event) => onInputChange(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: inputValue && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={onClearInput} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {inputValue.length > 0 && inputValue.length < minChars && (
          <Typography variant="caption" color="text.secondary">
            Enter at least {minChars} characters to search
          </Typography>
        )}

        <TopicSuggestions
          selectedTopicIds={selectedTopicIds}
          onAddTopic={onAddSuggestedTopic}
        />

        <Box sx={{ height: 300, display: "flex", flexDirection: "column" }}>
          {!shouldSearch && (
            <EmptyState
              icon={<SearchIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />}
              title="Start typing to search topics"
            />
          )}

          {shouldSearch && searchLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Searching...
              </Typography>
            </Box>
          )}

          {shouldSearch && !searchLoading && searchItems.length === 0 && (
            <EmptyState
              icon={<SearchIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />}
              title={<>No topics found for &quot;{searchQuery}&quot;</>}
            />
          )}

          {shouldSearch && !searchLoading && searchItems.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block", flexShrink: 0 }}
              >
                {resultsCount} results
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <TreeView
                  items={searchItems}
                  selectedItems={treeSelectedItems}
                  expandedItems={searchExpandedItems}
                  onSelectionChange={onSelectionChange}
                  selectionRules={SELECTION_PRESETS.flexible}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }
);

TopicsSearchTab.displayName = "TopicsSearchTab";
